var TicketControlLogic = Object.create(null),
    datefns = require('date-fns'),
    { TICKET_CONTROL_STATE } = require('../enums');

const UNIC_NUMBER_LENGTH = 9;
const UNIC_NUMBER_GROUP_LENGTH = 3;

/** @typedef {import('../models/TicketModel.js').TicketItem } TicketItem */

/**
 * @typedef {object} TicketControlInfos
 * @property {string} expirationDate - Date d'expiration au format dd/MM/yyyy
 * @property {boolean} isExpired - True si le ticket est expiré
 * @typedef {TicketItem & TicketControlInfos} TicketControlItem
 */

/**
 * @typedef {object} TicketControlFilters
 * @property {string} tab - État recherché, `all` pour tous les tickets
 * @property {string} search - Recherche sur le numéro, l'identifiant et le commentaire
 * @property {string} type - Nom du type de ticket, vide pour tous les types
 */

TicketControlLogic.addExpirationInfos = addExpirationInfos;
TicketControlLogic.getState = getState;
TicketControlLogic.findTicket = findTicket;
TicketControlLogic.filterTickets = filterTickets;
TicketControlLogic.countByState = countByState;
TicketControlLogic.getTypeNames = getTypeNames;
TicketControlLogic.formatUnicNumberInput = formatUnicNumberInput;

/**
 * Ajoute la date d'expiration aux tickets, un ticket reste valable le jour de son expiration.
 * Sans date d'expiration enregistrée, elle est calculée depuis la création du ticket
 * @param {Array<TicketItem>} tickets - Tickets
 * @param {number} durationValidity - Durée de validité en mois
 * @param {Date} today - Date du jour
 * @returns {Array<TicketControlItem>} Tickets avec leur date d'expiration
 * TU DONE 🗸
 */
function addExpirationInfos(tickets, durationValidity, today) {
  return tickets.map(ticket => {
    const expiration = ticket.expired_date ? new Date(ticket.expired_date) : datefns.addMonths(new Date(ticket.createdAt), durationValidity);

    return {
      ...ticket,
      expirationDate: datefns.format(expiration, 'dd/MM/yyyy'),
      isExpired: datefns.isBefore(datefns.startOfDay(expiration), datefns.startOfDay(today))
    };
  });
}

/**
 * Calcule l'état d'un ticket lors de son contrôle
 * @param {TicketControlItem} ticket - Ticket
 * @returns {string} Valeur de TICKET_CONTROL_STATE
 * TU DONE 🗸
 */
function getState(ticket) {
  if (ticket.is_used) {
    return TICKET_CONTROL_STATE.USED.value;
  }

  return ticket.isExpired ? TICKET_CONTROL_STATE.EXPIRED.value : TICKET_CONTROL_STATE.VALID.value;
}

/**
 * Recherche le ticket scanné, le QR code contient l'identifiant du ticket
 * @param {Array<TicketControlItem>} tickets - Tickets
 * @param {string} value - Identifiant ou numéro unique, avec ou sans N°
 * @returns {?TicketControlItem} Ticket trouvé
 * TU DONE 🗸
 */
function findTicket(tickets, value) {
  const search = value.replace(/^n°\s*/i, '').trim();
  const isId = /^\d+$/.test(search);

  return tickets.find(ticket => (isId ? ticket.id === parseInt(search, 10) : ticket.unic_number === search)) || null;
}

/**
 * Filtre les tickets de la liste, la recherche est un texte simple et non une expression régulière
 * @param {Array<TicketControlItem>} tickets - Tickets
 * @param {TicketControlFilters} filters - Filtres
 * @returns {Array<TicketControlItem>} Tickets filtrés
 * TU DONE 🗸
 */
function filterTickets(tickets, filters) {
  const search = filters.search.trim().toLowerCase();

  return tickets.filter(ticket => {
    const searchableText = `${ ticket.unic_number } ${ ticket.id } ${ ticket.comment || '' }`.toLowerCase();

    return (filters.tab === 'all' || getState(ticket) === filters.tab)
      && (!filters.type || ticket.ticket_type?.name === filters.type)
      && searchableText.includes(search);
  });
}

/**
 * Compte les tickets par état
 * @param {Array<TicketControlItem>} tickets - Tickets
 * @returns {{[key: string]: number}} Nombre de tickets par valeur de TICKET_CONTROL_STATE, plus `all`
 * TU DONE 🗸
 */
function countByState(tickets) {
  const counts = /** @type {{[key: string]: number}} */({ all: tickets.length });

  Object.values(TICKET_CONTROL_STATE).forEach(state => {
    counts[state.value] = tickets.filter(ticket => getState(ticket) === state.value).length;
  });

  return counts;
}

/**
 * Liste les noms des types présents dans les tickets
 * @param {Array<TicketControlItem>} tickets - Tickets
 * @returns {Array<string>} Noms triés
 * TU DONE 🗸
 */
function getTypeNames(tickets) {
  return [...new Set(tickets.map(ticket => ticket.ticket_type?.name).filter(Boolean))].sort();
}

/**
 * Aide à la saisie d'un numéro unique : majuscules et tiret tous les 3 caractères (ex: "A7K-QZ3-MPX").
 * Un numéro sans lettre reste tel quel, c'est le cas des anciens tickets (ex: "12-2025")
 * @param {string} value - Valeur saisie
 * @returns {string} Valeur mise en forme
 * TU DONE 🗸
 */
function formatUnicNumberInput(value) {
  if (/^[\d\s-]*$/.test(value)) {
    return value;
  }

  const characters = value.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, UNIC_NUMBER_LENGTH);

  return (characters.match(new RegExp(`.{1,${ UNIC_NUMBER_GROUP_LENGTH }}`, 'g')) || []).join('-');
}

module.exports = TicketControlLogic;
